/* eslint-disable react/sort-comp */
// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Utils
import rafThrottle from '../../../common/utils/lib/rafThrottle';
import { isAttemptingMultiSelect } from '../../element/selection/selectionUtils';
import { isRightButton } from '../../utils/domUtil';
import { getRelativePositionFromDomEvent } from '../../utils/dom/domEventUtil';
import * as pointLib from '../../../common/maths/geometry/point';
import { hasChanged } from '../../utils/react/propsComparisons';

const eventListenerNodeHasChanged = hasChanged('eventListenerNode');

/**
 * Attaches the DOM event listeners and invokes the state update methods as appropriate based on mouse moves.
 */
export default (DecoratedComponent) => {
    class SelectionMarqueeEventHandler extends React.Component {
        constructor(props) {
            super(props);
            this.eventsAttached = false;
        }

        componentWillMount() {
            this.attachMarqueeSelectionStartListener(this.props);
        }

        componentDidMount() {
            this.attachMarqueeSelectionStartListener(this.props);
        }

        componentDidUpdate(oldProps) {
            // Remove the event listeners and then re-attach them if we're changing the event listener node
            if (eventListenerNodeHasChanged(oldProps, this.props)) {
                this.removeEventListeners(oldProps);
            }

            this.attachMarqueeSelectionStartListener(this.props);
        }

        componentWillUnmount() {
            this.removeEventListeners(this.props);
        }

        attachMarqueeSelectionStartListener = (props) => {
            if (!props.eventListenerNode || this.eventsAttached) return;

            this.eventsAttached = true;

            props.eventListenerNode.addEventListener('mousedown', this.onMouseDown);
        };

        removeMarqueeSelectionStartListener = (props) => {
            props.eventListenerNode.removeEventListener('mousedown', this.onMouseDown);
        };

        attachMarqueeSelectionMoveListeners = () => {
            document.addEventListener('mousemove', this.throttledMouseMove);
            document.addEventListener('mouseup', this.onMouseUp);
        };

        removeMarqueeSelectionMoveListeners = () => {
            document.removeEventListener('mousemove', this.throttledMouseMove);
            document.removeEventListener('mouseup', this.onMouseUp);
        };

        removeEventListeners = (props) => {
            if (!props.eventListenerNode) return;
            this.eventsAttached = false;

            this.removeMarqueeSelectionStartListener(props);
            this.removeMarqueeSelectionMoveListeners(props);
        };

        onMouseDown = (event) => {
            const { startMarqueeSelect, eventListenerNode, canvasDocumentNode, dispatchDeselectAllElements } =
                this.props;

            const eventTargetIsEmptyCanvas = event.target === eventListenerNode || event.target === canvasDocumentNode;

            if (!eventTargetIsEmptyCanvas || event.button === 1) return;

            // Set focus to the document body so that keyboard shortcuts can be used again after interacting
            // with an iFrame
            window.focus();
            if (document.activeElement) document.activeElement.blur();

            event.preventDefault();
            if (isRightButton(event)) return dispatchDeselectAllElements();

            this.attachMarqueeSelectionMoveListeners();

            const appendMode = isAttemptingMultiSelect(event);

            // Visual coordinates with respect to the scroll area
            const mousePosition = this.getMousePosition(event);
            startMarqueeSelect({ ...mousePosition, appendMode });
        };

        onMouseMove = (event) => {
            // Visual coordinates with respect to the scroll area
            const mousePosition = this.getMousePosition(event);
            this.props.dragMarqueeSelectionTo(mousePosition);
        };

        throttledMouseMove = rafThrottle(this.onMouseMove); // eslint-disable-line react/sort-comp

        onMouseUp = (event) => {
            this.throttledMouseMove.cancel();
            this.props.finishMarqueeSelect();
            this.removeMarqueeSelectionMoveListeners(this.props);
        };

        getMousePosition = (event) => {
            const { offset, gridSize } = this.props;

            const mouseEventPosition = getRelativePositionFromDomEvent(event, this.props.eventListenerNode);

            // Only used in Inbox
            if (offset) {
                const scaledOffset = pointLib.scale(gridSize, offset);

                return pointLib.translate(scaledOffset, mouseEventPosition);
            }

            return mouseEventPosition;
        };

        render() {
            return <DecoratedComponent {...this.props} />;
        }
    }

    SelectionMarqueeEventHandler.propTypes = {
        eventListenerNode: PropTypes.object,
        canvasDocumentNode: PropTypes.object,
        startMarqueeSelect: PropTypes.func,
        dragMarqueeSelectionTo: PropTypes.func,
        finishMarqueeSelect: PropTypes.func,
        dispatchDeselectAllElements: PropTypes.func,
        deselectAll: PropTypes.func,

        offset: PropTypes.object,
        gridSize: PropTypes.number,
        zoomScale: PropTypes.number,
    };

    return SelectionMarqueeEventHandler;
};
